import React, { useMemo, useState } from 'react';
import {
  Table,
  Tabs,
  Input,
  Select,
  Tag,
  Space,
  Typography,
  Button,
  Spin,
  Badge,
  message,
} from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  getRegistryMappings,
  createRegistryMapping,
  getProjectIdProjectTypeMappings,
  createProjectIdProjectTypeMapping,
  getProjectTypes,
} from '../../api/project_type';
import { getGHGMethodologies } from '../../api/ghg';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

function getStatusFromCalyxType(calyxType: string | null | undefined) {
  return calyxType ? 'mapped' : 'needs-mapping';
}

function renderStatusTag(status: string) {
  if (status === 'mapped') return <Tag color="green">Mapped</Tag>;
  if (status === 'needs-mapping') return <Tag color="red">Needs Mapping</Tag>;
  return null;
}

function getProjectFinalCalyxType(row: any) {
  return row.calyx_project_type || row.project_id_mapped_project_type || null;
}

async function fetchRegistryMappingsData() {
  const data = await getRegistryMappings();
  const rows = Array.isArray(data) ? data : data.response || [];
  return rows.map((row: any, index: number) => ({
    ...row,
    id:
      row.id ??
      `${row.registry || 'REG'}-${row.registry_project_type || 'TYPE'}-${
        row.registry_methodology || 'METH'
      }-${row.protocol_version || 'VER'}-${index}`,
    total_inventory:
      typeof row.total_inventory === 'string'
        ? Number(row.total_inventory)
        : row.total_inventory,
  }));
}

async function fetchProjectMappingsData() {
  const data = await getProjectIdProjectTypeMappings();
  const rows = Array.isArray(data) ? data : data.response || [];
  return rows.map((row: any, index: number) => {
    const invRaw =
      row.project_inventory != null
        ? row.project_inventory
        : row.total_inventory;

    return {
      ...row,
      id: row.id ?? `${row.registry || 'REG'}-${row.project_id || 'PID'}-${index}`,
      project_inventory:
        typeof invRaw === 'string' ? Number(invRaw) : invRaw,
    };
  });
}

/**
 * IMPORTANT:
 * - calyxType, projectAttribute, calyxMethodology are used AS-IS.
 * - If you want to keep an old value, pass record.<field> from caller.
 * - If you clear in UI, we pass null, so payload sends null.
 */
async function updateRegistryCalyxProjectType(
  record: any,
  calyxType: string | null,
  projectAttribute: string | null,
  calyxMethodology: string | null,
) {
  const payload = {
    registry_project_type: record.registry_project_type,
    protocol_version: record.protocol_version || '',
    registry_methodology: record.registry_methodology,
    calyx_project_type: calyxType,
    project_attribute: projectAttribute,
    calyx_methodology: calyxMethodology,
  };

  await createRegistryMapping(payload);
  return {
    ...record,
    ...payload,
  };
}

/**
 * Project-level:
 * - calyxType → project_id_mapped_project_type (can be null)
 * - calyxMethodology → project_id_mapped_methodology (can be null)
 */
async function updateProjectIdCalyxProjectType(
  record: any,
  calyxType: string | null,
  calyxMethodology: string | null,
) {
  const payload = {
    project_id: record.project_id,
    ghg_standard_code: record.registry,
    project_id_mapped_project_type: calyxType,
    project_id_mapped_methodology: calyxMethodology,
  };
  await createProjectIdProjectTypeMapping(payload);
  return {
    ...record,
    project_id_mapped_project_type: calyxType,
    project_id_mapped_methodology: calyxMethodology,
  };
}

const registryMutationFn = ({
  record,
  calyxType,
  projectAttribute,
  calyxMethodology,
}: {
  record: any;
  calyxType: string | null;
  projectAttribute: string | null;
  calyxMethodology: string | null;
}) =>
  updateRegistryCalyxProjectType(
    record,
    calyxType,
    projectAttribute,
    calyxMethodology,
  );

const projectMutationFn = ({
  record,
  calyxType,
  calyxMethodology,
}: {
  record: any;
  calyxType: string | null;
  calyxMethodology: string | null;
}) => updateProjectIdCalyxProjectType(record, calyxType, calyxMethodology);

function ProjectTypeMapper() {
  const queryClient = useQueryClient();

  const [registrySearch, setRegistrySearch] = useState('');
  const [registryStatusFilter, setRegistryStatusFilter] = useState<
    'all' | 'mapped' | 'needs-mapping'
  >('all');
  const [projectSearch, setProjectSearch] = useState('');
  const [projectStatusFilter, setProjectStatusFilter] = useState<
    'all' | 'mapped' | 'needs-mapping'
  >('all');

  const {
    data: registryRows = [],
    isLoading: registryLoading,
    isFetching: registryFetching,
  } = useQuery({
    queryKey: ['registryMappings'],
    queryFn: fetchRegistryMappingsData,
  });

  const {
    data: projectRows = [],
    isLoading: projectLoading,
    isFetching: projectFetching,
  } = useQuery({
    queryKey: ['projectMappings'],
    queryFn: fetchProjectMappingsData,
  });

  const {
    data: projectTypesData,
    isLoading: projectTypesLoading,
  } = useQuery({
    queryKey: ['projectTypes'],
    queryFn: getProjectTypes,
  });

  const {
    data: ghgMethodologiesData,
    isLoading: ghgMethodologiesLoading,
  } = useQuery({
    queryKey: ['ghgMethodologies'],
    queryFn: getGHGMethodologies,
  });

  const calyxOptions = useMemo(() => {
    const raw = Array.isArray(projectTypesData)
      ? projectTypesData
      : projectTypesData?.response || [];
    return (raw as any[]).map((pt) => ({
      value: pt.display_name,
      label: pt.display_name,
    }));
  }, [projectTypesData]);

  const calyxMethodologyOptions = useMemo(() => {
    const raw = Array.isArray(ghgMethodologiesData)
      ? ghgMethodologiesData
      : ghgMethodologiesData?.response || [];
    return (raw as any[]).map((m) => ({
      value: m.display_name,
      label: m.display_name,
    }));
  }, [ghgMethodologiesData]);

  const {
    mutate: updateRegistryCalyx,
    isLoading: isUpdatingRegistry,
  } = useMutation(registryMutationFn, {
    onSuccess: () => {
      queryClient.invalidateQueries(['registryMappings']);
      queryClient.invalidateQueries(['projectMappings']);
      message.success('Registry mapping updated');
    },
    onError: () => {
      message.error('Failed to update registry mapping. Please try again.');
    },
  });

  const {
    mutate: updateProjectMapping,
    isLoading: isUpdatingProject,
  } = useMutation(projectMutationFn, {
    onSuccess: () => {
      queryClient.invalidateQueries(['projectMappings']);
      message.success('Project mapping updated');
    },
    onError: () => {
      message.error('Failed to update project mapping. Please try again.');
    },
  });

  const loading =
    registryLoading ||
    projectLoading ||
    projectTypesLoading ||
    ghgMethodologiesLoading ||
    registryFetching ||
    projectFetching ||
    isUpdatingRegistry ||
    isUpdatingProject;

  const filteredRegistryRows = useMemo(() => {
    const text = registrySearch.toLowerCase().trim();
    return (registryRows as any[]).filter((row) => {
      const status = getStatusFromCalyxType(row.calyx_project_type);
      if (registryStatusFilter !== 'all' && status !== registryStatusFilter) {
        return false;
      }
      if (!text) return true;
      const haystack = [
        row.registry,
        row.registry_project_type,
        row.registry_methodology,
        row.protocol_version,
        row.calyx_project_type || '',
        row.calyx_methodology || '',
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(text);
    });
  }, [registryRows, registrySearch, registryStatusFilter]);

  const filteredProjectRows = useMemo(() => {
    const text = projectSearch.toLowerCase().trim();
    return (projectRows as any[]).filter((row) => {
      const finalType = getProjectFinalCalyxType(row);
      const status = getStatusFromCalyxType(finalType);
      if (projectStatusFilter !== 'all' && status !== projectStatusFilter) {
        return false;
      }
      if (!text) return true;
      const haystack = [
        row.registry,
        row.project_id,
        row.registry_project_type,
        row.registry_methodology,
        row.protocol_version,
        finalType || '',
        row.project_id_mapped_methodology || '',
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(text);
    });
  }, [projectRows, projectSearch, projectStatusFilter]);

  // ---- Handlers: always pass string or null (never undefined) ----

  const handleRegistryCalyxMethodologyChange = (record: any, value: string | null) => {
    const methodology = value ?? null;
    updateRegistryCalyx({
      record,
      calyxType: record.calyx_project_type ?? null,
      projectAttribute: record.project_attribute ?? null,
      calyxMethodology: methodology,
    });
  };

  const handleRegistryCalyxChange = (record: any, value: string | null) => {
    const type = value ?? null;
    updateRegistryCalyx({
      record,
      calyxType: type,
      projectAttribute: record.project_attribute ?? null,
      calyxMethodology: record.calyx_methodology ?? null,
    });
  };

  const handleProjectCalyxChange = (record: any, value: string | null) => {
    const type = value ?? null;
    const methodology = record.project_id_mapped_methodology ?? null;
    updateProjectMapping({ record, calyxType: type, calyxMethodology: methodology });
  };

  const handleProjectCalyxMethodologyChange = (record: any, value: string | null) => {
    const methodology = value ?? null;
    const type =
      record.project_id_mapped_project_type ??
      record.calyx_project_type ??
      null;
    updateProjectMapping({ record, calyxType: type, calyxMethodology: methodology });
  };

  const handleRegistryProjectAttributeChange = (record: any, value: string | null) => {
    const attr = value ?? null;
    updateRegistryCalyx({
      record,
      calyxType: record.calyx_project_type ?? null,
      projectAttribute: attr,
      calyxMethodology: record.calyx_methodology ?? null,
    });
  };

  const registryColumns: any[] = [
    {
      title: 'Registry',
      dataIndex: 'registry',
      key: 'registry',
      width: 100,
    },
    {
      title: 'Registry Project Type',
      dataIndex: 'registry_project_type',
      key: 'registry_project_type',
    },
    {
      title: 'Methodology',
      dataIndex: 'registry_methodology',
      key: 'registry_methodology',
    },
    {
      title: 'Version',
      dataIndex: 'protocol_version',
      key: 'protocol_version',
      width: 120,
    },
    {
      title: 'Total Inventory',
      dataIndex: 'total_inventory',
      key: 'total_inventory',
      width: 140,
      align: 'right',
      render: (value: number | null) => (value != null ? value.toLocaleString() : '—'),
    },
    {
      title: 'Calyx Project Type',
      dataIndex: 'calyx_project_type',
      key: 'calyx_project_type',
      width: 260,
      render: (value: string | null, record: any) => (
        <Select
          allowClear
          placeholder="Select Calyx Project Type"
          value={value || undefined}
          options={calyxOptions}
          onChange={(val) => handleRegistryCalyxChange(record, val ?? null)}
          style={{ width: '100%' }}
          showSearch
          optionFilterProp="label"
        />
      ),
    },
    {
      title: 'Calyx Methodology',
      dataIndex: 'calyx_methodology',
      key: 'calyx_methodology',
      width: 260,
      render: (value: string | null, record: any) => {
        const baseOptions = calyxMethodologyOptions;
        const options =
          value && !baseOptions.some((o) => o.value === value)
            ? [{ value, label: value }, ...baseOptions]
            : baseOptions;

        return (
          <Select
            allowClear
            placeholder="Select Calyx Methodology"
            value={value || undefined}
            options={options}
            onChange={(val) =>
              handleRegistryCalyxMethodologyChange(record, val ?? null)
            }
            style={{ width: '100%' }}
            showSearch
            optionFilterProp="label"
          />
        );
      },
    },
    {
      title: 'Project Attribute',
      dataIndex: 'project_attribute',
      key: 'project_attribute',
      width: 220,
      render: (value: string | null, record: any) => (
        <Select
          allowClear
          placeholder="Select project attribute"
          value={value || undefined}
          options={[
            { value: 'Removals', label: 'Removals' },
            { value: 'Removals + Avoidance', label: 'Removals + Avoidance' },
            { value: 'Avoidance', label: 'Avoidance' },
          ]}
          onChange={(val) =>
            handleRegistryProjectAttributeChange(record, val ?? null)
          }
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 130,
      render: (_: any, record: any) => {
        const status = getStatusFromCalyxType(record.calyx_project_type);
        return renderStatusTag(status);
      },
    },
  ];

  const projectColumns: any[] = [
    {
      title: 'Project ID',
      dataIndex: 'project_id',
      key: 'project_id',
      width: 130,
    },
    {
      title: 'Registry',
      dataIndex: 'registry',
      key: 'registry',
      width: 90,
    },
    {
      title: 'Inventory',
      dataIndex: 'project_inventory',
      key: 'project_inventory',
      align: 'right',
      width: 120,
      render: (value: number | null) => (value != null ? value.toLocaleString() : '—'),
    },
    {
      title: 'Calyx Project Type',
      key: 'calyx_project_type',
      width: 260,
      render: (_: any, record: any) => {
        if (record.calyx_project_type) {
          return <Text>{record.calyx_project_type}</Text>;
        }
        return (
          <Select
            allowClear
            placeholder="Select Calyx Project Type"
            value={record.project_id_mapped_project_type || undefined}
            options={calyxOptions}
            onChange={(val) => handleProjectCalyxChange(record, val ?? null)}
            style={{ width: '100%' }}
            showSearch
            optionFilterProp="label"
          />
        );
      },
    },
    {
      title: 'Calyx Methodology',
      dataIndex: 'project_id_mapped_methodology',
      key: 'project_id_mapped_methodology',
      width: 260,
      render: (value: string | null, record: any) => {
        const baseOptions = calyxMethodologyOptions;
        const options =
          value && !baseOptions.some((o) => o.value === value)
            ? [{ value, label: value }, ...baseOptions]
            : baseOptions;

        return (
          <Select
            allowClear
            placeholder="Select Calyx Methodology"
            value={value || undefined}
            options={options}
            onChange={(val) =>
              handleProjectCalyxMethodologyChange(record, val ?? null)
            }
            style={{ width: '100%' }}
            showSearch
            optionFilterProp="label"
          />
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 130,
      render: (_: any, record: any) => {
        const finalType = getProjectFinalCalyxType(record);
        const status = getStatusFromCalyxType(finalType);
        return renderStatusTag(status);
      },
    },
  ];

  const registryNeedsMappingCount = (registryRows as any[]).filter(
    (r) => getStatusFromCalyxType(r.calyx_project_type) === 'needs-mapping',
  ).length;

  const projectNeedsMappingCount = (projectRows as any[]).filter((r) => {
    const finalType = getProjectFinalCalyxType(r);
    return getStatusFromCalyxType(finalType) === 'needs-mapping';
  }).length;

  return (
    <div
      style={{
        padding: 24,
        background: '#f5f5f5',
        minHeight: '100vh',
        boxSizing: 'border-box',
      }}
    >
      <Spin spinning={loading} tip="Loading mappings…">
        <div
          style={{
            marginBottom: 16,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <Title level={3} style={{ marginBottom: 0 }}>
              Project Type Mapper
            </Title>
            <Text type="secondary">
              Map registry project types to Calyx project types and manage project-level mappings.
            </Text>
          </div>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              queryClient.invalidateQueries(['registryMappings']);
              queryClient.invalidateQueries(['projectMappings']);
              queryClient.invalidateQueries(['projectTypes']);
              queryClient.invalidateQueries(['ghgMethodologies']);
            }}
          >
            Refresh
          </Button>
        </div>

        <Tabs defaultActiveKey="registry" type="card">
          <TabPane
            key="registry"
            tab={
              <Space>
                Registry-level
                <Badge
                  count={registryNeedsMappingCount}
                  size="small"
                  style={{ backgroundColor: '#fa541c' }}
                />
              </Space>
            }
          >
            <div
              style={{
                margin: '16px 0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Input
                prefix={<SearchOutlined />}
                placeholder="Search registry, project type, methodology, version…"
                value={registrySearch}
                onChange={(e) => setRegistrySearch(e.target.value)}
                style={{ maxWidth: 360 }}
              />
              <Space>
                <Text type="secondary">Show:</Text>
                <Select
                  value={registryStatusFilter}
                  onChange={setRegistryStatusFilter}
                  style={{ width: 200 }}
                  options={[
                    { value: 'all', label: 'All combinations' },
                    { value: 'needs-mapping', label: 'Needs mapping' },
                    { value: 'mapped', label: 'Mapped' },
                  ]}
                />
              </Space>
            </div>

            <Table
              columns={registryColumns}
              dataSource={filteredRegistryRows}
              rowKey="id"
              size="middle"
              pagination={{ pageSize: 50 }}
            />
          </TabPane>

          <TabPane
            key="project"
            tab={
              <Space>
                Project-level
                <Badge
                  count={projectNeedsMappingCount}
                  size="small"
                  style={{ backgroundColor: '#fa541c' }}
                />
              </Space>
            }
          >
            <div
              style={{
                margin: '16px 0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Input
                prefix={<SearchOutlined />}
                placeholder="Search project ID, registry…"
                value={projectSearch}
                onChange={(e) => setProjectSearch(e.target.value)}
                style={{ maxWidth: 360 }}
              />
              <Space>
                <Text type="secondary">Show:</Text>
                <Select
                  value={projectStatusFilter}
                  onChange={setProjectStatusFilter}
                  style={{ width: 200 }}
                  options={[
                    { value: 'all', label: 'All projects' },
                    { value: 'needs-mapping', label: 'Needs mapping' },
                    { value: 'mapped', label: 'Mapped' },
                  ]}
                />
              </Space>
            </div>

            <Table
              columns={projectColumns}
              dataSource={filteredProjectRows}
              rowKey="id"
              size="middle"
              pagination={{ pageSize: 50 }}
            />
          </TabPane>
        </Tabs>
      </Spin>
    </div>
  );
}

export default ProjectTypeMapper;
